import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AvatarIcon, CountryFlag } from "@/lib/identity";
import { Crown, Trophy } from "@phosphor-icons/react";

/**
 * EmbedCard — server-render target for the Playwright share-card generator.
 *
 * Route: `/embed/card/:handle` (public, no shell).
 *
 * Renders a 1200×630 "NK Card" — the layout matches ShareCardModal's inline
 * card so the offline OG image feels the same as the in-app share. The page
 * strips all chrome (no header/footer/scrollbars) so Playwright can
 * screenshot the `[data-testid="embed-card"]` element pixel-perfectly.
 *
 * After the data + fonts load we set `data-testid="embed-card-ready"` on
 * the document body so the backend renderer knows when to snap.
 */
export default function EmbedCard() {
  const { handle } = useParams();
  const [profile, setProfile] = useState(null);
  const [season, setSeason] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/season/profile/${handle}`).then(r => {
        // Backend returns HTTP 200 with {error:'not_found'} for unknown handles
        if (r.data?.error || !r.data?.user) throw new Error("not_found");
        setProfile(r.data);
      }),
      api.get("/season/current").then(r => setSeason(r.data)).catch(() => {}),
    ]).catch(() => setError(true));
  }, [handle]);

  // Signal readiness to the Playwright renderer
  useEffect(() => {
    if (profile && !error) {
      // Wait one paint cycle so fonts/avatars finish, then mark ready
      requestAnimationFrame(() => {
        const el = document.createElement("div");
        el.setAttribute("data-testid", "embed-card-ready");
        el.style.display = "none";
        document.body.appendChild(el);
      });
    }
  }, [profile, error]);

  // Body must be sized exactly 1200×630 so the screenshot has no scrollbars
  useEffect(() => {
    document.documentElement.style.margin = "0";
    document.body.style.margin = "0";
    document.body.style.background = "#0A0A0B";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.margin = "";
      document.body.style.margin = "";
      document.body.style.background = "";
      document.body.style.overflow = "";
    };
  }, []);

  if (error) {
    return (
      <div style={{ width: 1200, height: 630 }}
           className="flex items-center justify-center bg-nk-bg text-nk-text"
           data-testid="embed-card">
        <div className="text-center">
          <p className="font-display text-4xl">Player not found</p>
          <p className="mt-2 text-nk-mute font-mono">nkognitogames.com</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ width: 1200, height: 630 }} className="bg-nk-bg" />
    );
  }

  const u = profile.user || {};
  const s = profile.stats || {};
  const rank = profile.season_rank;
  const titles = profile.champion_titles || [];
  const seasonNumber = season?.season_number || 1;
  const country = u.country || "";
  const countryLabel = country === "GH" ? "Ghana" : country === "NG" ? "Nigeria" : "the world";

  return (
    <div
      data-testid="embed-card"
      style={{
        width: 1200,
        height: 630,
        background:
          "radial-gradient(ellipse at top left, rgba(212,175,55,0.08) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(11,61,46,0.35) 0%, transparent 60%), linear-gradient(135deg, #0d1210 0%, #0A0A0B 100%)",
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Fraunces', Georgia, serif",
        color: "#FAFAFA",
      }}
    >
      {/* Grain overlay */}
      <div
        style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.35\'/%3E%3C/svg%3E")',
          opacity: 0.15,
          mixBlendMode: "overlay",
        }}
      />

      {/* Gold rule top-left → bottom-right */}
      <div style={{
        position: "absolute", left: 50, top: 50, right: 50, height: 1,
        background: "linear-gradient(90deg, rgba(212,175,55,0.6), rgba(212,175,55,0) 80%)",
      }} />

      {/* NKognito wordmark */}
      <div style={{
        position: "absolute", left: 60, top: 68,
        fontFamily: "'JetBrains Mono', monospace", fontSize: 13,
        letterSpacing: "0.28em", color: "#D4AF37", textTransform: "uppercase",
      }}>
        NKognito · Season {seasonNumber}
      </div>

      {/* Main content */}
      <div style={{ position: "absolute", left: 60, top: 130, right: 60, bottom: 60, display: "flex", gap: 32 }}>
        {/* Left column — avatar + hall of fame */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, minWidth: 220 }}>
          <div style={{
            width: 200, height: 200, borderRadius: 16,
            background: "#131316", border: "2px solid rgba(212,175,55,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 40px rgba(212,175,55,0.18)",
          }}>
            <AvatarIcon id={u.avatar} size={140} />
          </div>
          {titles.length > 0 && (
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "6px 12px", borderRadius: 999,
              border: "1px solid rgba(212,175,55,0.5)",
              background: "rgba(212,175,55,0.08)",
              fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
              color: "#D4AF37", letterSpacing: "0.12em",
            }}>
              <Crown size={12} weight="fill" />
              {titles.length}× CHAMPION
            </div>
          )}
        </div>

        {/* Right column — name, country, stats */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            <h1 style={{
              fontSize: 78, fontWeight: 500, letterSpacing: "-0.02em",
              lineHeight: 1, margin: 0, color: "#FAFAFA",
            }}>
              {u.username || "Player"}
            </h1>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
              {country && <CountryFlag country={country} size={22} />}
              <span style={{ fontSize: 22, color: "#A1A1AA" }}>
                Playing for {countryLabel}
              </span>
              {u.nk_plus_active && (
                <span style={{
                  marginLeft: 10, padding: "3px 10px", borderRadius: 4,
                  background: "rgba(212,175,55,0.15)", color: "#D4AF37",
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                  letterSpacing: "0.15em",
                }}>NK+</span>
              )}
            </div>
          </div>

          {/* Stats grid */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20,
            padding: "24px 0", borderTop: "1px solid rgba(255,255,255,0.08)",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}>
            <Stat label="Season score" value={(s.season_score || 0).toLocaleString()} highlight />
            <Stat label="Season rank" value={rank ? `#${rank}` : "—"} />
            <Stat label="Knowledge rating" value={(s.knowledge_rating || 1000).toLocaleString()} />
          </div>

          {/* Footer — Africa's Arena tagline */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
            color: "#71717A", letterSpacing: "0.22em", textTransform: "uppercase",
          }}>
            <span>Africa&apos;s Competitive Knowledge Arena</span>
            <span style={{ color: "#D4AF37" }}>nkognitogames.com</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
        color: "#71717A", letterSpacing: "0.18em", textTransform: "uppercase",
        marginBottom: 8,
      }}>{label}</div>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace", fontSize: 36, fontWeight: 500,
        color: highlight ? "#D4AF37" : "#FAFAFA", lineHeight: 1,
      }}>{value}</div>
    </div>
  );
}
